<template>
  <div class="fixed top-0 bottom-0 left-0 right-0 bg-white" data-cy="otp-modal">
    <div class="grid grid-rows-[1f,auto,1fr] text-center h-screen">
      <div class="grid-start-1">
        <div class="flex items-center justify-between p-6">
          <button @click="emit('toggleOtp')" class="flex justify-center items-center" data-cy="otp-back-btn">
            <font-awesome-icon icon="fa-solid fa-chevron-left" class="w-6 h-6" />
          </button>

          <span class="gfold:text-xl text-2xl font-poppins font-semibold">
            {{ $t('PhoneVerification.Title') }}
          </span>

          <button>
            <div class="w-6 h-6"></div>
          </button>
        </div>

        <p class="m-4 text-lg font-poppins font-semibold">
          {{ $t('PhoneVerification.EnterCode') }}
        </p>

        <p class="text-sm">{{ $t('PhoneVerification.SMSDiscription') }}</p>
        <p class="text-xs font-semibold" data-cy="otp-full-phone-number">{{ fullPhoneNumber }}</p>

        <div class="p-8">
          <div class="border-b-2 w-36 mx-auto border-b-primary-green flex justify-center">
            <input
              v-for="index in 6"
              :key="index"
              :id="`otp-input-${index}`"
              :data-cy="`otp-input-${index}`"
              ref="inputRefs"
              @focus="handleInputFocus"
              @keydown="handleOtpInputChange"
              type="tel"
              maxlength="1"
              class="w-6 focus:outline-none text-lg font-semibold text-center focus:bg-gray-200"
              placeholder="&bull;"
              autocomplete="off"
            />
          </div>
        </div>
        <p>
          {{ $t('PhoneVerification.OTPExpireTime') }}
          <span data-cy="firebase-timeout" class="text-primary-blue text-base font-semibold">{{ firebaseOTPTimeout }}</span>
        </p>
      </div>
      <!--  -->
      <div class="grid start-3">
        <div class="flex flex-col mt-auto mb-24" data-cy="otp-not-arrive-button">
          <p>{{ $t('PhoneVerification.OTPNotArrive') }}</p>
          <p>
            <button
              @click="resendOtpFn"
              data-cy="resend-button"
              :disabled="disabled"
              :class="disabled ? 'text-gray-600' : 'text-primary-blue underline font-semibold cursor-pointer'"
            >
              {{ $t('PhoneVerification.Resend') }}

              <span v-if="disabled"> ({{ resendTime }}) </span>
            </button>
            or
            <button @click="emit('toggleOtp')" :disabled="disabled" :class="disabled ? 'text-gray-600' : 'text-primary-blue underline font-semibold cursor-pointer'">
              {{ $t('PhoneVerification.ChangePhone') }}
            </button>
          </p>
        </div>
      </div>
    </div>

    <div v-if="showMsgError" class="fixed top-0 right-0 bottom-0 left-0 bg-black bg-opacity-70 flex items-center justify-center" data-cy="otp-msg-error-modal">
      <div class="max-w-md bg-white rounded-xl shadow-xl overflow-hidden relative m-8 grow">
        <p class="text-2xl font-poppins font-semibold px-6 py-4" data-cy="otp-msg-error-text">
          {{ $t('PhoneVerification.InvalidOtp') }}
        </p>

        <p class="px-6 py-2 pb-8">{{ $t('PhoneVerification.TryAgainInfo') }}</p>

        <div class="flex justify-end">
          <button @click="emit('toggleShowMsgError')" class="flex justify-center font-semibold text-xl text-blue-900 p-4 pr-6" data-cy="otp-msg-error-close-btn">
            OK
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
input[type='tel'] {
  caret-color: transparent;
}

input[type='tel']:focus::placeholder {
  color: transparent;
}
</style>

<script setup lang="ts">
import { useCountdownTimer } from '@pi-xcels/utils';
import { computed, onMounted, onUnmounted, readonly, ref } from 'vue';

defineProps<{
  fullPhoneNumber: string;
  showMsgError: boolean;
}>();
const emit = defineEmits(['toggleOtp', 'onSubmitOtp', 'toggleShowMsgError', 'resendOtp']);

// Firebase default OTP timeout 15 mins
const defaultTime = 15 * 60 * 1000;

// Resend Timeout is 90 seconds
const resendTimeout = 90 * 1000;

const { restart: restartTimer, resetAndStop: stopTimer, timeLeft, timePassed } = useCountdownTimer(readonly(ref(defaultTime)));

const disabled = computed(() => timeLeft.value > defaultTime - resendTimeout);
const resendTime = computed(() => (resendTimeout - timePassed.value) / 1000);

const firebaseOTPTimeout = computed(() => {
  const minutes = Math.floor(timeLeft.value / (60 * 1000));
  const seconds = (timeLeft.value % 60000) / 1000;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
});

const resendOtpFn = () => {
  emit('resendOtp');
  restartTimer();
};

const otpNumber = ref('');
const inputRefs = ref<HTMLInputElement[]>([]);

const handleInputFocus = () => {
  inputRefs.value[otpNumber.value.length].focus();
};

const handleOtpInputChange = (e: KeyboardEvent) => {
  e.preventDefault();

  const allowedKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'Backspace'];

  if (allowedKeys.includes(e.key) && inputRefs.value) {
    const inputIdNumber = Number((e.target as HTMLInputElement).id.split('-')[2]);

    if (e.key === 'Backspace') {
      if (otpNumber.value === '') return;

      otpNumber.value = otpNumber.value.slice(0, -1);
      inputRefs.value[inputIdNumber - 2].value = '';
      inputRefs.value[inputIdNumber - 2].focus();
    } else {
      otpNumber.value += e.key;
      inputRefs.value[inputIdNumber - 1].value = e.key;

      if (inputIdNumber === 6) {
        emit('onSubmitOtp', otpNumber.value);
        otpNumber.value = '';

        for (const inputRef of inputRefs.value) inputRef.value = '';
        inputRefs.value[0].focus();
        return;
      }

      inputRefs.value[inputIdNumber].focus();
    }
  }
};

onMounted(() => {
  restartTimer();
});

onUnmounted(() => {
  stopTimer();
});
</script>
